import { Input, InputGroup, Label } from '@slal/ui/forms';
import { FormProvider, useForm, useWatch, yupResolver } from '@slal/ui/react-hook-form/core';
import { Field, RadioFieldGroup, TextField } from '@slal/ui/react-hook-form/fields';
import { normalizeMonetaryAmount } from '@slal/ui/react-hook-form/utils';
import { P } from '@slal/ui/typography';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { MaxWidthContent, Notification, StyledForm, yesOrNoOptions } from '~/components';
import { useAnalytics, useApplication, useApplicationContext } from '~/hooks';
import { pensionSchema, validationMessages } from '~/schemas';
import { ApplicationFormKeys } from '~/types';
import { ApplicationButtons } from '../ApplicationButtons';
import { ApplicationFormSubmitHandler } from '../types';
import { PensionFieldSet, StyledPensionFormFields } from './styles';
import { initialPension, provider } from './utils';

export const PensionForm = () => {
    const { setSession, getSession } = useApplication();
    const {
        pensionValues: { total },
    } = useApplicationContext();

    const form = useForm({
        mode: 'onTouched',
        resetOptions: {
            keepDirty: false,
            keepErrors: false,
            keepTouched: true,
        },
        defaultValues: initialPension,
        resolver: yupResolver(pensionSchema(total, true)),
    });

    const navigate = useNavigate();

    const { pushEvent } = useAnalytics();

    const addPension = useCallback(() => {
        const existingPensions = getSession(ApplicationFormKeys.PENSION_TRANSFER)?.pensions || [];

        const newPension = form.getValues();
        const updatedPensions = [...existingPensions, newPension];

        setSession({ pensions: updatedPensions });
    }, [form, setSession, getSession]);

    const handleSubmit: ApplicationFormSubmitHandler = useCallback(
        () => async event => {
            event.preventDefault();
            const valid = await form.trigger();
            if (valid) {
                addPension();
                navigate('/application/pensions');
                pushEvent({ eventLabel: 'Next button click - successful - all fields completed' });
            } else {
                pushEvent({ eventLabel: 'Next button click - unsuccessful - all fields not completed' });
            }
        },
        [form, pushEvent]
    );

    return (
        <MaxWidthContent>
            <FormProvider {...form}>
                <StyledForm onSubmit={handleSubmit()}>
                    <PensionFormFields />
                    <ApplicationButtons backTo="/application/pensions" />
                </StyledForm>
            </FormProvider>
        </MaxWidthContent>
    );
};

export const PensionFormFields = () => {
    const [moneyWithdrawn, employerContribution] = useWatch({
        name: [`moneyWithdrawn`, `employerContribution`],
    });

    return (
        <StyledPensionFormFields>
            <Input type="hidden" name={`provider`} value={provider} />
            <PensionFieldSet aria-label="Pension information">
                <Field label="Policy number" labelFor={`policyNumber`}>
                    <TextField name={`policyNumber`} />
                </Field>
                <Field
                    helpMessage="You can find this on your latest statement"
                    label="Current value"
                    labelFor={`value`}
                >
                    <InputGroup>
                        <Label>£</Label>
                        <TextField name={`value`} setValueAs={normalizeMonetaryAmount} />
                    </InputGroup>
                </Field>
                <Field
                    label="Have you already taken money out of this pension?"
                    errorFor={`moneyWithdrawn`}
                    hideValidationMessage={validationMessages.notYesValue}
                >
                    <RadioFieldGroup name={`moneyWithdrawn`} options={yesOrNoOptions} stacked />
                </Field>
                {moneyWithdrawn === 'Yes' && <MoneyWithdrawnNotification />}
                <Field
                    label="Is your employer currently paying into this pension?"
                    errorFor={`employerContribution`}
                    hideValidationMessage={validationMessages.notYesValue}
                >
                    <RadioFieldGroup name={`employerContribution`} options={yesOrNoOptions} stacked />
                </Field>
                {employerContribution === 'Yes' && <EmployerContributionNotification />}
            </PensionFieldSet>
        </StyledPensionFormFields>
    );
};

const EmployerContributionNotification = () => {
    return (
        <Notification heading="Wait until you've left your employer before transferring" level="warning">
            <P>This means you won't miss any employer contributions.</P>
            <P>If you still want to continue with this transfer, call us on 0345 266 8013. Call charges will vary.</P>
        </Notification>
    );
};

const MoneyWithdrawnNotification = () => {
    return (
        <Notification heading="Call us to transfer" level="warning">
            <P>
                As you've already taken money out of this pension, you need to call us on 0345 266 8013 if you want to
                transfer it. Call charges will vary.
            </P>
        </Notification>
    );
};
